import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { TEMPLATES } from "@/constants/testIds";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, Copy, FileText, Calendar } from "lucide-react";

const CATEGORY_COLORS = {
  Hospital: "bg-orange-100 text-orange-700 border-orange-200",
  Médicos: "bg-rose-100 text-rose-700 border-rose-200",
  Enfermería: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Administración: "bg-slate-100 text-slate-700 border-slate-200",
  TI: "bg-indigo-100 text-indigo-700 border-indigo-200",
  Gerencia: "bg-amber-100 text-amber-700 border-amber-200",
  General: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/templates");
      setTemplates(data);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/templates/${confirmDelete.id}`);
      toast.success("Plantilla eliminada");
      setConfirmDelete(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar");
    }
  };

  return (
    <div className="space-y-6" data-testid={TEMPLATES.list}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-orange-600 mb-1">
            Diseño
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Plantillas de firma</h1>
          <p className="text-sm text-slate-500 mt-1">
            {templates.length} plantillas · Cada modificación crea una nueva versión.
          </p>
        </div>
        <Button
          onClick={() => navigate("/templates/new")}
          className="bg-orange-600 hover:bg-orange-700 text-white transition-colors"
          data-testid={TEMPLATES.newBtn}
        >
          <Plus className="w-4 h-4 mr-2" strokeWidth={1.75} />
          Nueva plantilla
        </Button>
      </div>

      {loading && (
        <div className="text-sm text-slate-400 py-8 text-center">Cargando...</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map((t) => (
          <Card key={t.id} className="p-5 border-slate-200 shadow-sm hover:border-orange-300 transition-colors group">
            <div className="flex items-start justify-between mb-3">
              <div className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wider border ${CATEGORY_COLORS[t.category] || CATEGORY_COLORS.General}`}>
                {t.category}
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                {t.image_strategy === "cid" && (
                  <span className="px-1.5 py-0.5 rounded border border-orange-200 bg-orange-50 text-orange-700 text-[10px] font-semibold tracking-wider"
                    title="Imágenes embebidas (CID)" data-testid={TEMPLATES.cidBadge(t.id)}>
                    CID
                  </span>
                )}
                <span className="flex items-center gap-1"><Copy className="w-3 h-3" strokeWidth={1.75} /> v{t.version}</span>
              </div>
            </div>
            <div className="flex items-start gap-2 mb-2">
              <FileText className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" strokeWidth={1.75} />
              <h3 className="text-base font-semibold text-slate-900 leading-tight">{t.name}</h3>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed line-clamp-2 min-h-[32px]">
              {t.description || "Sin descripción."}
            </p>
            <div className="mt-4 flex items-center gap-2 text-[11px] text-slate-400">
              <Calendar className="w-3 h-3" strokeWidth={1.75} />
              <span>Actualizada {new Date(t.updated_at).toLocaleDateString("es")}</span>
              {t.is_active && (
                <Badge className="ml-auto bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-emerald-200 text-[10px]">
                  Activa
                </Badge>
              )}
            </div>
            <div className="mt-4 pt-4 border-t border-slate-200 flex gap-2">
              <Button
                size="sm" variant="outline"
                onClick={() => navigate(`/templates/${t.id}`)}
                className="flex-1 border-slate-200 hover:bg-orange-50 hover:text-orange-700 hover:border-orange-200"
                data-testid={`template-edit-${t.id}`}
              >
                <Edit3 className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.75} />
                Editar
              </Button>
              <Button
                size="sm" variant="outline"
                onClick={() => setConfirmDelete(t)}
                className="border-slate-200 hover:bg-red-50 hover:text-red-700 hover:border-red-200"
                data-testid={TEMPLATES.deleteBtn(t.id)}
              >
                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Eliminar plantilla</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            ¿Estás seguro de eliminar <strong>{confirmDelete?.name}</strong>? Esta acción
            también eliminará sus versiones y desasignará a los usuarios.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)} className="border-slate-200">
              Cancelar
            </Button>
            <Button onClick={doDelete} className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-template-btn">
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
